/**
 * How a dashboard's filter bar is built, and how its values reach the tiles.
 *
 * PURE — no React, no fetching, no query client. Everything here is a function
 * of the tiles and their models, which is what makes the two genuinely
 * difficult decisions on this surface testable in isolation:
 *
 *  1. WHICH fields the bar offers, when different tiles sit on subject areas
 *     with different vocabularies — and which of them the author pinned.
 *  2. WHAT HAPPENS when a dashboard filter and a tile's own filter name the
 *     same dimension.
 *
 * Both are decided once, here, rather than inside a component where they would
 * be re-derived per render and impossible to assert about.
 */
import type { FilterField } from '@clockwise/report-kit';
import type {
  DashboardFilterDecl,
  DashboardTile,
  Dimension,
  FilterNode,
  FilterRule,
  ReportModel,
  ReportSpec,
} from '@clockwise/semantic-core';
import {
  canFilter,
  findDimension,
  isFilterGroup,
  type ReportPeriodPreset,
  sharedPeriods,
  validateFilters,
  withResolvedPeriod,
} from '@clockwise/semantic-core';
import { DATE_RANGE_PRESETS } from '@clockwise/shared/date-range';
import { saysSomething } from '@/lib/filter-url';
import { grainOfDimension, isTrendedMetric, metricWindow } from './metric-window';

// The one test of whether a rule narrows anything, shared with saved reports.
export { saysSomething };

/**
 * One tile that actually asks a question, with its question resolved.
 *
 * Report tiles need a lookup (their spec lives on a saved report); KPI tiles
 * carry theirs inline. Text and tombstone tiles produce nothing — see
 * {@link resolveTiles}.
 */
export interface ResolvedTile {
  tileId: string;
  spec: ReportSpec;
  model: ReportModel;
}

/**
 * A dimension the bar can offer, and which tiles it would reach.
 *
 * A `FilterField` — the shared bar's definition of a field — plus its reach.
 * The model is the FIRST tile's that has the dimension: members are the values
 * a dimension takes in the rows this caller may see, so any tile carrying it
 * gives the same key space (`customer` is `customer_id` in every model that
 * has it), and picking one avoids N identical lookups for one chip.
 */
export interface FilterCandidate extends FilterField {
  /** Tile ids this filter would reach. */
  appliesTo: string[];
}

/**
 * Every dimension ANY tile can filter on, with its coverage.
 *
 * THE UNION, not the intersection, and not all of it on the bar. The bar used
 * to seed itself with every dimension all the tiles shared — on a dashboard of
 * three timesheet tiles that was every filterable column in the model, twenty
 * chips nobody had asked for. Now these are what the "+ Filter" menu offers;
 * what is ON the bar is what the author pinned and what the reader added.
 *
 * MATCHED BY DIMENSION ID, never by label. Two models naming `customer` mean
 * the same customers — both compile it to `customer_id` — whereas two models
 * whose dimensions merely READ alike routinely mean different things. Matching
 * on labels would silently union the two Sr. Controllers into one chip that
 * filters half the dashboard by the wrong person.
 */
export function deriveDashboardFilters(tiles: readonly ResolvedTile[]): FilterCandidate[] {
  const byDimension = new Map<
    string,
    { dimension: Dimension; model: ReportModel; tiles: string[] }
  >();

  for (const tile of tiles) {
    for (const dimension of tile.model.dimensions) {
      if (!canFilter(dimension)) continue;
      const existing = byDimension.get(dimension.id);
      if (existing) existing.tiles.push(tile.tileId);
      else byDimension.set(dimension.id, { dimension, model: tile.model, tiles: [tile.tileId] });
    }
  }

  const labelCounts = new Map<string, number>();
  for (const { dimension } of byDimension.values()) {
    labelCounts.set(dimension.label, (labelCounts.get(dimension.label) ?? 0) + 1);
  }

  return [...byDimension.values()]
    .map(({ dimension, model, tiles: appliesTo }) => ({
      id: dimension.id,
      label: dimension.label,
      description: dimension.description,
      // Only where it is actually needed: a hint on every chip is noise, and a
      // hint on the one ambiguous pair is the whole point.
      hint: (labelCounts.get(dimension.label) ?? 0) > 1 ? dimension.description : undefined,
      model,
      appliesTo,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * The fields the bar is handed, as the viewer should see them.
 *
 * A saved decl's LABEL wins over the dimension's — it exists to tell apart two
 * chips that read alike. And while EDITING, a field that does not reach every
 * tile says so: an author deciding whether to pin a filter is asking exactly
 * "what would this narrow", and has nothing else to answer it with. A reader
 * does not need telling — a tile a filter missed says so itself, under its own
 * numbers, which is where the question actually arises.
 */
export function barFields(
  candidates: readonly FilterCandidate[],
  saved: readonly DashboardFilterDecl[],
  opts: { editing: boolean; tileCount: number },
): FilterCandidate[] {
  const labels = new Map(saved.flatMap((d) => (d.label ? [[d.dimension, d.label] as const] : [])));
  return candidates.map((c) => {
    const label = labels.get(c.id) ?? c.label;
    const partial = opts.tileCount > 0 && c.appliesTo.length < opts.tileCount;
    const note =
      opts.editing && partial ? `applies to ${c.appliesTo.length} of ${opts.tileCount}` : undefined;
    return label === c.label && note === undefined ? c : { ...c, label, note };
  });
}

/**
 * The fields the author PINNED, in their stored order.
 *
 * A decl naming a dimension no tile can filter any more is DROPPED rather than
 * shown inert. It happens whenever the last tile using that subject area is
 * removed, and a chip that filters nothing is worse than no chip.
 */
export function pinnedFields(
  candidates: readonly FilterCandidate[],
  saved: readonly DashboardFilterDecl[],
): string[] {
  const known = new Set(candidates.map((c) => c.id));
  return saved.map((d) => d.dimension).filter((id) => known.has(id));
}

/**
 * The decls to save for a new pin list.
 *
 * `pinned` is what the bar showed — only fields a tile can take RIGHT NOW. A
 * saved pin outside `known` (its tile still loading, or a tombstone for a
 * report that was unshared) was never on the bar to be toggled, so it is KEPT,
 * after the ones that were. Rebuilding from the bar's list alone deleted it on
 * the next Save.
 *
 * Keeps an existing decl's label override: unpinning and re-pinning a chip
 * should not quietly rename it, and nothing else on this surface writes one.
 */
export function withPinned(
  saved: readonly DashboardFilterDecl[],
  pinned: readonly string[],
  known: ReadonlySet<string>,
): DashboardFilterDecl[] {
  const byId = new Map(saved.map((d) => [d.dimension, d]));
  return [
    ...pinned.map((dimension) => byId.get(dimension) ?? { dimension }),
    ...saved.filter((d) => !known.has(d.dimension) && !pinned.includes(d.dimension)),
  ];
}

/**
 * Tiles that ask a question, paired with their model.
 *
 * TEXT AND TOMBSTONE TILES ARE EXCLUDED, and that exclusion is load-bearing
 * rather than tidiness. They carry no model, so counting them would make the
 * intersection empty the moment somebody adds a heading — a note tile would
 * silently delete the whole filter bar, which is an unexplainable behaviour to
 * hit and an unguessable one to diagnose.
 *
 * A report tile whose report cannot be resolved (deleted, or unshared since
 * the dashboard was saved) is also skipped: it draws as a tombstone, and a
 * filter cannot reach a tile that has no question to narrow.
 */
export function resolveTiles(
  tiles: readonly DashboardTile[],
  specOf: (tile: DashboardTile) => ReportSpec | undefined,
  modelOf: (modelId: string) => ReportModel | undefined,
): ResolvedTile[] {
  const out: ResolvedTile[] = [];
  for (const tile of tiles) {
    if (tile.type !== 'report' && tile.type !== 'metric') continue;
    const spec = specOf(tile);
    if (!spec) continue;
    const model = modelOf(spec.model);
    if (!model) continue;
    out.push({ tileId: tile.id, spec, model });
  }
  return out;
}

/**
 * Stands in for a tile whose model this viewer's catalog does not carry.
 *
 * Offers every period but `custom`: the strictest any model is today. The
 * catalog leaves out a model the viewer may not read (Budget vs Actuals without
 * `customer.budget.view`), and the period this viewer picks travels in the URL
 * to people who DO see that tile — so an unseen tile must narrow the period
 * as if it were seen. A retired model reads the same way; it runs nothing, so
 * the cost is a missing Custom range, not a wrong number.
 */
const UNSEEN_MODEL: Pick<ReportModel, 'periods'> = {
  periods: DATE_RANGE_PRESETS.filter((p) => p !== 'custom'),
};

/**
 * The periods the dashboard's ONE period may offer: the ones every tile that
 * asks a question offers.
 *
 * Decided per tile rather than from `resolveTiles`, because that list drops
 * exactly the tiles that matter here:
 *
 *  - STILL LOADING (`settled` false): a linked report not yet fetched could be
 *    on any model, so Custom is held back until every tile is known. Offering
 *    it late is harmless; withdrawing it after a reader picked it is the bug.
 *    A hang never settles, so it stays held back.
 *  - A MODEL THE VIEWER CANNOT SEE: narrows as {@link UNSEEN_MODEL}.
 *  - A TOMBSTONE (report deleted or unshared — `specOf` has nothing for a
 *    settled tile): runs nothing, so it narrows nothing. Counting it would take
 *    Custom off the dashboard for good over a tile nobody can repair from here.
 *
 * Text and unknown tiles ask no question and are skipped, as everywhere else.
 */
export function dashboardPeriods(
  tiles: readonly DashboardTile[],
  specOf: (tile: DashboardTile) => ReportSpec | undefined,
  modelOf: (modelId: string) => ReportModel | undefined,
  settled: boolean,
): readonly ReportPeriodPreset[] {
  const offering: Pick<ReportModel, 'periods'>[] = [];
  for (const tile of tiles) {
    if (tile.type !== 'report' && tile.type !== 'metric') continue;
    const spec = specOf(tile);
    if (!spec) continue;
    offering.push(modelOf(spec.model) ?? UNSEEN_MODEL);
  }
  const shared = sharedPeriods(offering);
  return settled ? shared : shared.filter((p) => p !== 'custom');
}

/** How one dashboard filter relates to one tile. */
export type FilterState =
  /** The tile took it. */
  | 'applied'
  /** The tile's facts do not carry that dimension. Say so; never hide the tile. */
  | 'notApplicable'
  /**
   * The tile COULD take this filter; nothing is selected.
   *
   * Distinct from `notApplicable`, which is a statement about the facts rather
   * than about the selection. Collapsing the two reads as "no tile can be
   * filtered by Customer" on a dashboard where every tile can — exactly
   * backwards for a coverage summary.
   */
  | 'unset'
  /**
   * The tile filters that dimension itself, and its own value stands.
   *
   * NOT "pinned", which is what this was called before chips could be pinned
   * to the bar: a pinned chip is the author's, and this is the tile's.
   */
  | 'tileOwned';

/**
 * The tile's OWN answer to the question a dashboard filter asks, if it has one.
 *
 * TOP LEVEL ONLY, and that restriction is the whole of what the node shape
 * forced. A rule inside a group is one arm of a question the dashboard did not
 * ask — `(Customer is Acme or Hours >= 40)` does not say "this tile is about
 * Acme" — so reading it as the tile's customer filter would report a tile as
 * having answered something it never did, and editing it would rewrite a
 * disjunction the author wrote deliberately.
 */
function ownFilter(nodes: readonly FilterNode[], dimension: string): FilterNode | undefined {
  // PAUSED rules do not count. A paused rule is on the tile and out of its
  // query, so treating it as the tile's own value suppressed the dashboard's
  // filter AND reported the tile as `tileOwned` — the bar claiming a value
  // stood while the tile showed everything.
  return nodes.find((node) => !isFilterGroup(node) && node.field === dimension && !node.paused);
}

/**
 * The bar's rules that say something AND that `model` can run — valid for its
 * columns and operators. Anything else is dropped rather than failing the tile.
 */
function liveRules(active: readonly FilterNode[], model: ReportModel): FilterRule[] {
  return active.filter(saysSomething).filter((rule) => validateFilters(model, [rule]).ok);
}

/** Is this dimension SET on the bar — any rule on it that says something? */
export function isSet(active: readonly FilterNode[], dimension: string): boolean {
  return active.some((node) => saysSomething(node) && node.field === dimension);
}

export function filterStateFor(
  tile: ResolvedTile,
  dimension: string,
  active: readonly FilterNode[],
): FilterState {
  const dim = findDimension(tile.model, dimension);
  if (!dim || !canFilter(dim)) return 'notApplicable';
  // Checked before "applied": a tile that already filters this dimension keeps
  // its own value, so reporting it as applied would describe the dashboard
  // filter as having done something it did not do.
  if (ownFilter(tile.spec.filters, dimension)) return 'tileOwned';
  return liveRules(active, tile.model).some((r) => r.field === dimension) ? 'applied' : 'unset';
}

/** Which tiles a filter reached, and which it did not. For the FR-4.5 notices. */
export function filterCoverage(
  tiles: readonly ResolvedTile[],
  dimension: string,
  active: readonly FilterNode[],
): Record<FilterState, string[]> {
  const out: Record<FilterState, string[]> = {
    applied: [],
    notApplicable: [],
    unset: [],
    tileOwned: [],
  };
  for (const tile of tiles) out[filterStateFor(tile, dimension, active)].push(tile.tileId);
  return out;
}

/**
 * A linked report's spec with the TILE's own filters ANDed on.
 *
 * The tile's filters sit BETWEEN the report and the dashboard: they are the
 * tile's own answer, so the dashboard's merge rule sees them as the tile's
 * (`tileOwned`) and a bar filter on the same field does not override them.
 * Appended at the top level, never merged into the report's own nodes, so a
 * tile can only narrow the report it shows.
 *
 * Their ids are namespaced — `validateFilters` refuses two nodes sharing one,
 * and the tile's ids were minted without looking at the report's. The suffix
 * is DETERMINISTIC for the same reason `effectiveSpec`'s is: a fresh id per
 * render would be a new query per render.
 *
 * No tile filters returns the SAME spec object, so a tile without any keeps
 * its memoised identity.
 */
export function withTileFilters(spec: ReportSpec, tileFilters: readonly FilterNode[]): ReportSpec {
  if (tileFilters.length === 0) return spec;
  const taken = new Set(spec.filters.map((node) => node.id));
  const added = tileFilters.map((node) => {
    const base = `tile.${node.id}`;
    let id = base.slice(0, 64);
    for (let n = 2; taken.has(id); n++) id = `${base.slice(0, 63 - String(n).length)}_${n}`;
    taken.add(id);
    return { ...node, id };
  });
  return { ...spec, filters: [...spec.filters, ...added] };
}

export interface EffectiveSpecInput {
  spec: ReportSpec;
  model: ReportModel;
  /** The bar's rules — pinned and reader-added alike. */
  active: readonly FilterNode[];
  /** The dashboard's window, already resolved. */
  period: { preset?: ReportSpec['period']; from: string; to: string };
  /** How the tile draws, overriding the saved report's own choice. */
  view?: { viz: ReportSpec['viz']; stacked: boolean };
  /** Injectable for tests; defaults to now. */
  today?: Date;
}

/**
 * The spec a tile actually runs.
 *
 * THE MERGE RULE: per dimension, the TILE WINS and the dashboard fills gaps.
 * If the tile already filters a dimension, its value stands; the dashboard's
 * value applies only where the tile said nothing.
 *
 * The property that makes this safe is that it can only ever NARROW — whatever
 * the operator, since every rule is ANDed on. A
 * dashboard filter adds a constraint where there was none, and never replaces
 * one — so no arrangement of the bar can make a tile show more than its saved
 * report does. The alternative (dashboard overrides tile) can widen: a report
 * saved as "ACME only" would show every customer the moment somebody cleared
 * the bar, which is a disclosure decided by a control that looks cosmetic.
 *
 * The dashboard's value is ANDed onto the TOP LEVEL of the tile's node list
 * and the tile's own groups are left alone — see {@link ownFilter}. Against
 * the old record shape "set Customer" had one reading; against nodes it does
 * not, and the top level is the only place a dashboard can add a condition
 * without editing a question somebody else wrote.
 *
 * A dashboard rule this MODEL cannot run is dropped rather than passed through
 * — a dimension it does not have, an operator its column does not offer, or a
 * chip with no value yet. The compiler refuses all three, so forwarding them
 * would turn "this tile cannot be filtered by Employee" into a failed tile
 * with a validation error in it.
 *
 * The PERIOD is not a filter and is not merged: it replaces the tile's window
 * outright. Every model exposes a date column and every spec carries `from`/
 * `to`, so the dashboard's period always applies — which is why there is one
 * period control rather than one per subject area.
 */
export function effectiveSpec(input: EffectiveSpecInput): ReportSpec {
  const { spec, model, active, period, view, today } = input;

  const filters: FilterNode[] = [...spec.filters];
  const taken = new Set(filters.map((node) => node.id));
  for (const rule of liveRules(active, model)) {
    // The tile's own filter wins — see the merge rule above.
    if (ownFilter(spec.filters, rule.field)) continue;
    // Namespaced, so a bar rule can never collide with one of the tile's own
    // ids — `validateFilters` refuses a spec with two nodes sharing one. Taken
    // from the rule rather than generated: a fresh id would make this spec a
    // different object on every render, which is a different query key and
    // therefore a tile that refetches forever.
    const base = `dash.${rule.id}`;
    let id = base.slice(0, 64);
    // A NUMBERED suffix, so every pass tries a new id: re-truncating and
    // appending the same character produced the same string once the id was
    // long enough, and the loop never ended.
    for (let n = 2; taken.has(id); n++) id = `${base.slice(0, 63 - String(n).length)}_${n}`;
    taken.add(id);
    filters.push({ ...rule, id });
  }

  const viz = view?.viz ?? spec.viz;
  const rows = spec.rows;

  // A METRIC QUERIES FURTHER BACK than the dashboard asks, so its sparkline
  // has history and its comparison has something to compare to. The dashboard
  // window still decides WHICH period is reported — that is
  // `selectMetricPeriods`, which is handed the original window separately.
  const window =
    isTrendedMetric({ viz, rows }) && rows[0]
      ? metricWindow({
          from: period.from,
          to: period.to,
          grain: grainOfDimension(rows[0]),
          today,
        })
      : { from: period.from, to: period.to };

  const merged: ReportSpec = {
    ...spec,
    filters,
    // The MACRO is dropped for a widened metric window: it no longer describes
    // the dates below it, and `withResolvedPeriod` would helpfully recompute
    // them back to the dashboard's own window and undo the widening.
    period: window.from === period.from && window.to === period.to ? period.preset : undefined,
    from: window.from,
    to: window.to,
    ...(view ? { viz: view.viz, stacked: view.stacked } : {}),
  };

  // A macro period has to be RE-ANSWERED wherever a spec arrives: a dashboard
  // saved under "current month" is a dashboard about whatever month it is
  // opened in, and the stored `from`/`to` are only the answer from the day it
  // was saved.
  return withResolvedPeriod(merged, today);
}
